/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { forwardRef, useEffect, useMemo, useRef } from "react";

import cn from "classnames";

import useDevAttributes from "hooks/useDevAttributes";
import { useUI } from "state";

import { DeleteIcon } from "components/icons";
import Stack from "components/ui/Stack";
import Heading from "components/ui/Heading";
import Text from "components/ui/Text";
import { Button } from "components/ui/Button";

import { useLoading } from "state/LoadingContext";

type Props = {
  title?: string | React.ReactNode;
  note?: string | React.ReactNode;
  onSave?: any;
  onDelete?: any;
  children?: React.ReactNode;
  onClose?: any;
  isLoading?: boolean;
  isFull?: boolean;
  hasBorder?: boolean;
  hasPadding?: boolean | "x" | "y";
  disabled?: boolean;
  className?: string;
  leftButtonProps?: any;
  rightButtonProps?: any;
  titleProps?: any;
  footerProps?: any;
};

const leftBtnProps = {
  label: "Cancel",
  variant: "primary-outline",
  size: "xl",
};

const rightBtnProps = {
  label: "Save",
  variant: "primary",
  size: "xl",
};

const Modal = ({
  title,
  onSave,
  children,
  onClose,
  onDelete,
  isLoading: propIsLoading,
  disabled = false,
  className = "",
  leftButtonProps = {},
  rightButtonProps = {},
  titleProps = {},
  footerProps = {},
}: Props) => {
  const { closeModal } = useUI();
  const { isLoading: contextIsLoading } = useLoading();
  const isLoading = propIsLoading || contextIsLoading;

  const _rightButtonProps = {
    onClick: onSave,
    isLoading,
    ...rightBtnProps, // default props
    ...rightButtonProps, // override default props
    disabled,
  };

  const _leftButtonProps = {
    onClick: onClose || closeModal,
    ...leftBtnProps, // default props
    ...leftButtonProps, // override default props
  };

  return (
    <ModalContainer className={className} test-id="5c5Hzkwv">
      {/* for custom header. */}

      {!!title && (
        <ModalHeader
          title={title}
          onDelete={onDelete}
          titleProps={titleProps}
          test-id="5sIMnvLH"
        />
      )}

      <ModalBody test-id="a2f9et7m">{children}</ModalBody>
      {/* for custom footer. */}

      <ModalFooter
        onClose={onClose}
        onSave={onSave}
        buttons={[_leftButtonProps, _rightButtonProps]}
        isLoading={isLoading}
        {...footerProps}
        test-id="7tZtF49l"
      />
    </ModalContainer>
  );
};

const ModalContainer = forwardRef<HTMLDivElement, Props>(
  ({ children, className = "", isFull = false }, ref) => {
    return (
      <Stack
        ref={ref}
        tabIndex={-1}
        className={cn(
          "sm:!min-w-[390px] bg-primary !max-h-[calc(100vh-20px)] mt-[20px] sm:max-h-[90%] min-h-[170px] shadow-md shadow-black border border-primary-600 max-md:w-[calc(100vw-20px)]",
          className,
          {
            "max-w-sm": !isFull,
            "w-full max-w-[80%] sm:max-w-[100%] !min-w-[calc(100vw-100px)]":
              isFull,
          },
        )}
        test-id="ZONOLp4N"
      >
        {children}
      </Stack>
    );
  },
);

const ModalHeader = ({
  title,
  note = "",
  onDelete,
  children,
  titleProps = {},
  hasBorder = true,
  className,
  hasPadding = true,
}: Props) => {
  // @ts-ignore
  const { deleteModalAttr } = useDevAttributes({
    deleteModalAttr: "delete-instance-modal",
  });

  const _titleProps = {
    as: "h3",
    className: "text-white",
    ...titleProps,
  };

  const Wrapper = useMemo(
    () =>
      ({ children, className }: Props) => (
        <div
          className={cn(className, {
            "border-b-2 border-c-gray-700 border-0": hasBorder,
            "py-5": hasPadding === "y",
            "px-5": hasPadding === "x",
            "!pb-3 p-5": hasPadding === true,
          })}
          test-id="hZ0cNNan"
        >
          {children}
        </div>
      ),
    [],
  );

  return (
    <Wrapper
      className={cn("flex space-x-2 justify-between items-center", className)}
      test-id="5wIrhTDO"
    >
      {title && (
        <div className="flex-1">
          <Heading
            {..._titleProps}
            className={cn("flex-1", _titleProps.className)}
            test-id="h1svwUG7"
          >
            {title}
          </Heading>

          {note && <Text className="text-xs text-light-gray">{note}</Text>}
        </div>
      )}

      {children ||
        (!!onDelete && (
          <div className="max-w-1/5" test-id="asl7agVu">
            <Button
              size="large"
              onClick={onDelete}
              {...deleteModalAttr}
              test-id="VXHV5joG"
            >
              <div
                className=" ml-auto text-alert text-right"
                test-id="Sf6fKJF1"
              >
                <DeleteIcon size="large" test-id="iIjFiOLo" />
              </div>
            </Button>
          </div>
        ))}
    </Wrapper>
  );
};

const ModalBody = ({ children, hasPadding = true, className = "" }: Props) => {
  const divRef = useRef(null);
  useEffect(() => {
    // @ts-ignore
    if (divRef.current) divRef.current.focus();
  }, []);

  return (
    <div
      ref={divRef}
      tabIndex={1}
      className={cn(
        "overflow-auto focus:outline-none",
        {
          "py-5": hasPadding === "y",
          "px-5": hasPadding === "x",
          "p-4": hasPadding === true,
        },
        className,
      )}
      test-id="ZW_-X79D"
    >
      {children}
    </div>
  );
};

interface IModalFooter extends Props {
  buttons?: { [key in string]: any }[];
  hasMaxWidth?: boolean;
  isLoading?: boolean;
}

const ModalFooter = ({
  children,
  className = "",
  buttons,
  hasBorder = true,
  hasMaxWidth = true,
  hasPadding = true,
  isLoading,
}: IModalFooter) => {
  const Wrapper = useMemo(
    () =>
      ({ children, className }: Props) => (
        <div
          className={cn({
            "border-t-2 p-1 border-c-gray-700 border-0": hasBorder,
            "py-5": hasPadding === "y",
            "px-5": hasPadding === "x",
            "p-5": hasPadding === true,
          })}
          test-id="whHxGGoz"
        >
          <div
            className={cn({ "max-w-[380px] mx-auto": hasMaxWidth }, className)}
            test-id="nlYMDMR3"
          >
            {children}
          </div>
        </div>
      ),
    [],
  );

  if (children) {
    return (
      <Wrapper className={className} test-id="GJHehlDW">
        {children}
      </Wrapper>
    );
  }

  if (!buttons) return null;
  const [firstBtn, rightBtn] = buttons;
  return (
    <Wrapper
      className={cn(className, "flex items-center space-x-2")}
      test-id="Sr1ZnrSf"
    >
      {/* @ts-ignore */}
      <Button {...firstBtn} test-id="XX0Lg7I1">
        {firstBtn.children || firstBtn.label}
      </Button>

      {/* @ts-ignore */}

      <Button
        {...rightBtn}
        test-id="sjgPGfkm"
        isLoading={isLoading || rightBtn.isLoading}
        disabled={isLoading || rightBtn.disabled}
      >
        {rightBtn.children || rightBtn.label}
      </Button>
    </Wrapper>
  );
};

Modal.Container = ModalContainer;
Modal.Header = ModalHeader;
Modal.Body = ModalBody;
Modal.Footer = ModalFooter;

export default Modal;
